<template>
    <van-popup
        v-model:show="kfState.show"
        position="bottom"
        round
        :style="{ height: '82%', maxHeight: '82vh', backgroundColor: headerBg }"
        class="kf-drawer-popup"
        :safe-area-inset-bottom="false"
        :close-on-click-overlay="true"
        :overlay-style="{ backgroundColor: 'rgba(0, 0, 0, 0.65)', backdropFilter: 'blur(3px)' }"
        @closed="onClosed"
    >
        <div class="kf-drawer-container" :style="{ backgroundColor: headerBg }">
            <!-- 沉浸式高级视觉头部栏（根据每个站点的专属品牌颜色、名称、Logo 自动匹配渲染） -->
            <div class="kf-drawer-header" :style="{ backgroundColor: headerBg, color: headerTextColor }">
                <div class="kf-header-left">
                    <div class="kf-avatar-box">
                        <img v-if="kfAvatar" :src="kfAvatar" class="kf-avatar" :style="{ borderColor: themeColor }" @error="onAvatarError" />
                        <div v-else class="kf-avatar-svg" :style="{ borderColor: themeColor }">
                            <svg viewBox="0 0 36 36" width="30" height="30" fill="none">
                                <circle cx="18" cy="18" r="17" fill="#232733" />
                                <circle cx="18" cy="14" r="6" fill="#e2e8f0" />
                                <path d="M7 30c0-6 5-9 11-9s11 3 11 9" fill="#e2e8f0" />
                                <path d="M8 14a2 2 0 0 1 2-2v4a2 2 0 0 1-2-2zM26 14a2 2 0 0 0-2-2v4a2 2 0 0 0 2-2z" :fill="themeColor" />
                                <path d="M10 12c0-4.5 3.5-8 8-8s8 3.5 8 8" :stroke="themeColor" stroke-width="2" stroke-linecap="round" fill="none" />
                            </svg>
                        </div>
                        <span class="kf-online-dot" :style="{ borderColor: headerBg, backgroundColor: '#10b981' }"></span>
                    </div>
                    <div class="kf-info-box">
                        <div class="kf-name" :style="{ color: headerTextColor }">{{ kfTitle }}</div>
                        <div class="kf-status-text" :style="{ color: subTextColor }">
                            <span class="status-pulse" :style="{ backgroundColor: '#10b981' }"></span>
                            <span>官方认证 · 在线服务</span>
                        </div>
                    </div>
                </div>

                <!-- 关闭按钮置于右上角 -->
                <div class="kf-header-right">
                    <div class="kf-close-btn" @click="closeKf" title="关闭客服">
                        <van-icon name="cross" size="15px" :color="headerTextColor" />
                    </div>
                </div>
            </div>

            <!-- 加载过渡占位 -->
            <div v-if="loading" class="kf-drawer-loading">
                <van-loading type="spinner" :color="themeColor" size="26px" vertical>
                    正在连接客服系统...
                </van-loading>
            </div>

            <!-- 客服 H5 页面 iframe 容器 -->
            <div class="kf-iframe-wrapper">
                <iframe
                    v-if="kfState.show && kfState.url"
                    ref="iframeRef"
                    :src="kfState.url"
                    class="kf-drawer-iframe"
                    :style="iframeStyle"
                    frameborder="0"
                    allow="camera; microphone; clipboard-write;"
                    @load="onIframeLoad"
                ></iframe>
            </div>
        </div>
    </van-popup>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'
import { kfState, closeKf } from '@/uilts/common'
import common from '@/uilts/common'

const loading = ref(true)
const iframeRef = ref(null)
const avatarError = ref(false)

// 初始从缓存快速读取站点信息，杜绝异步等待时的白屏或名字跳变
const loadWebInfo = () => {
    try {
        const cached = sessionStorage.getItem('web')
        if (cached) {
            const data = JSON.parse(cached)
            if (data && typeof data === 'object') return data
        }
    } catch (e) {}
    return {}
}
const webInfo = ref(loadWebInfo())

// 1. 动态标题：严格根据每个站点配置的站点名动态呈现，如 "BOSS玩家专属客服"
const kfTitle = computed(() => {
    // 业务端若显式传入自定义标题（如特定活动/商品客服）
    if (kfState.title && kfState.title !== '专属客服') {
        return kfState.title
    }
    // 站点专属客服名配置
    if (webInfo.value?.web_service_wechat_name) {
        return webInfo.value.web_service_wechat_name
    }
    if (webInfo.value?.web_kf_name) {
        return webInfo.value.web_kf_name
    }
    // 站点主名称动态拼装
    const siteName = webInfo.value?.web_name || ''
    if (siteName) {
        return siteName.endsWith('客服') ? siteName : `${siteName}专属客服`
    }
    return '官方专属客服'
})

// 2. 动态头像/Logo：严格根据每个站点后台配置的 Logo / 客服头像展示
const kfAvatar = computed(() => {
    if (avatarError.value) return ''
    if (kfState.avatar) return kfState.avatar
    if (webInfo.value?.web_service_wechat_avatar) return webInfo.value.web_service_wechat_avatar
    if (webInfo.value?.web_logo) return webInfo.value.web_logo
    return ''
})

const onAvatarError = () => {
    avatarError.value = true
}

// 3. 动态颜色主题：与各个站点的头部导航背景色（--color5）、文字色（--color6）、主强调色（--color2）完全联动
const headerBg = computed(() => {
    return webInfo.value?.web_rgb_five || 'var(--color5, #10111A)'
})

const headerTextColor = computed(() => {
    return webInfo.value?.web_rgb_six || 'var(--color6, #ffffff)'
})

const themeColor = computed(() => {
    return webInfo.value?.web_rgb_two || 'var(--color2, #FFDB00)'
})

const subTextColor = computed(() => {
    return webInfo.value?.web_rgb_four || 'var(--color4, rgba(255, 255, 255, 0.7))'
})

// 设备与视口检测
const isMobileMode = computed(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 650
})

const iframeStyle = computed(() => {
    // 全端（手机端与PC电脑端）统一加载 index/mobile 模版，其头部高度固定为 50px
    // 精准上移 50px 刚好隐藏 iframe 内的原生冗余头部，完整展示专属头部栏与下方常见问题横滑标签栏，输入框完美贴底
    return {
        marginTop: '-50px',
        height: 'calc(100% + 50px)'
    }
})

const onIframeLoad = () => {
    loading.value = false
}

const onClosed = () => {
    loading.value = true
}

// 跨窗口消息监听
const handleMessage = (e) => {
    if (!e || !e.data) return
    if (e.data === 'close' || e.data.action === 'close' || e.data.type === 'close') {
        closeKf()
    }
}

const refreshWebInfo = async () => {
    try {
        const web = await common.getWeb()
        if (web) {
            webInfo.value = web
        }
    } catch (e) {}
}

watch(() => kfState.show, async (newVal) => {
    if (newVal) {
        loading.value = true
        avatarError.value = false
        await refreshWebInfo()
    }
})

onMounted(async () => {
    window.addEventListener('message', handleMessage)
    await refreshWebInfo()
})

onBeforeUnmount(() => {
    window.removeEventListener('message', handleMessage)
})
</script>

<!-- 全局覆盖 Vant 弹窗底色与圆角，彻底消除两角白边，自适应各站点主色 -->
<style lang="scss">
.van-popup.kf-drawer-popup {
    border-radius: 20px 20px 0 0 !important;
    background-color: var(--color5, #10111A) !important;
    background: var(--color5, #10111A) !important;
    overflow: hidden !important;
    box-shadow: 0 -12px 36px rgba(0, 0, 0, 0.45) !important;
    z-index: 3000 !important;
    border: none !important;

    &::after, &::before {
        display: none !important;
    }
}
</style>

<style scoped lang="scss">
.kf-drawer-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: var(--color5, #10111A) !important;
    border-radius: 20px 20px 0 0 !important;
    position: relative;
    overflow: hidden;

    /* 各站点动态品牌质感头部 */
    .kf-drawer-header {
        height: 52px;
        min-height: 52px;
        background-color: var(--color5, #10111A);
        border-radius: 20px 20px 0 0 !important;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 16px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        z-index: 10;
        position: relative;
        overflow: hidden;

        .kf-header-left {
            display: flex;
            align-items: center;
            gap: 10px;
            flex: 1;
            overflow: hidden;

            .kf-avatar-box {
                position: relative;
                width: 36px;
                height: 36px;
                flex-shrink: 0;

                .kf-avatar {
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    object-fit: cover;
                    border: 1.5px solid var(--color2, rgba(255, 255, 255, 0.85));
                    background: #fff;
                }

                .kf-avatar-svg {
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #232733;
                    border: 1.5px solid var(--color2, rgba(255, 255, 255, 0.85));
                    overflow: hidden;
                }

                .kf-online-dot {
                    position: absolute;
                    bottom: -1px;
                    right: -1px;
                    width: 9px;
                    height: 9px;
                    border-radius: 50%;
                    background: #10b981;
                    border: 2px solid var(--color5, #10111A);
                }
            }

            .kf-info-box {
                display: flex;
                flex-direction: column;
                justify-content: center;
                overflow: hidden;

                .kf-name {
                    font-size: 15px;
                    font-weight: 600;
                    color: var(--color6, #ffffff);
                    line-height: 1.25;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    letter-spacing: 0.2px;
                }

                .kf-status-text {
                    font-size: 11px;
                    color: var(--color4, rgba(255, 255, 255, 0.65));
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    margin-top: 2px;

                    .status-pulse {
                        width: 6px;
                        height: 6px;
                        border-radius: 50%;
                        background: #10b981;
                        box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.35);
                    }
                }
            }
        }

        .kf-header-right {
            display: flex;
            align-items: center;
            flex-shrink: 0;

            .kf-close-btn {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: rgba(255, 255, 255, 0.12);
                border: 1px solid rgba(255, 255, 255, 0.18);
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);

                &:active {
                    background: rgba(255, 255, 255, 0.24);
                    transform: scale(0.92);
                }
            }
        }
    }

    .kf-drawer-loading {
        position: absolute;
        top: 52px;
        left: 0;
        right: 0;
        bottom: 0;
        background: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 5;

        :deep(.van-loading__text) {
            margin-top: 10px;
            font-size: 13px;
            color: #666;
        }
    }

    .kf-iframe-wrapper {
        flex: 1;
        width: 100%;
        height: calc(100% - 52px);
        background: #ffffff;
        overflow: hidden;
        position: relative;

        .kf-drawer-iframe {
            width: 100%;
            border: none;
            display: block;
        }
    }
}
</style>
